import { ThemeEvents } from '@theme/events';

class ProductMediaGallery extends HTMLElement {
  videoId = 'f38ef5dbfb524bc2849c31cfe3b67255';
  observedMedia = new WeakSet();

  isTargetVideo = (video) => {
    return [video.src, video.currentSrc,
      ...Array.from(video.querySelectorAll('source'), source => source.src)
    ].some(url => url?.includes(this.videoId));
  };

  connectedCallback() {
    this.closestSection = this.closest('.shopify-section, dialog');
    this.closestSection?.addEventListener(
      ThemeEvents.variantUpdated,
      this.updateMediaGallery
    );

    this.observedMedia = new WeakSet();

    this.visibilityObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const media = entry.target;
        const visible = entry.isIntersecting &&
          entry.intersectionRatio >= 0.5;

        if (media instanceof HTMLVideoElement) {
          if (visible && !document.hidden) {
            media.muted = true;
            media.play().catch(() => {
              // Les commandes restent disponibles si le navigateur
              // bloque la lecture automatique.
            });
          } else {
            media.pause();
          }
        } else if (visible && !document.hidden) {
          // Certains thèmes chargent la vidéo après un clic.
          const button = media.querySelector('button');
          if (button) {
            this.visibilityObserver.unobserve(media);
            button.click();
          }
        }
      }
    }, { threshold: [0, 0.5] });

    this.mutationObserver = new MutationObserver(this.prepareVideos);
    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });

    document.addEventListener('visibilitychange', this.handleVisibility);
    this.prepareVideos();
  }

  prepareVideos = () => {
    this.querySelectorAll('video').forEach(video => {
      if (!this.isTargetVideo(video) || this.observedMedia.has(video)) return;

      this.observedMedia.add(video);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      this.visibilityObserver.observe(video);
    });

    this.querySelectorAll('deferred-media').forEach(media => {
      if (this.observedMedia.has(media)) return;

      const template = media.querySelector('template');
      const video = template?.content.querySelector('video');
      if (!video || !this.isTargetVideo(video)) return;

      // Applique le mode muet avant la création du lecteur.
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      this.observedMedia.add(media);
      this.visibilityObserver.observe(media);
    });
  };

  handleVisibility = () => {
    this.querySelectorAll('video').forEach(video => {
      if (!this.isTargetVideo(video)) return;

      if (document.hidden) {
        video.pause();
      } else {
        this.visibilityObserver.unobserve(video);
        this.visibilityObserver.observe(video);
      }
    });
  };

  disconnectedCallback() {
    this.closestSection?.removeEventListener(
      ThemeEvents.variantUpdated,
      this.updateMediaGallery
    );
    this.visibilityObserver?.disconnect();
    this.mutationObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  updateMediaGallery = (event) => {
    const newMediaGallery = event.detail.data.html.querySelector(
      'product-media-gallery slider-component'
    );
    const currentMediaGallery = this.querySelector('slider-component');

    if (!newMediaGallery || !currentMediaGallery) return;

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      currentMediaGallery.replaceWith(newMediaGallery);
      window.ShopifyXR?.setupXRElements();
      this.prepareVideos();
    }
  };
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}